import React, { useEffect, useRef, useState } from "react";
import { api, fileToBase64, openBase64Pdf } from "../lib/api";
import { useConfirm } from "../context/ConfirmContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import {
  Plus, Trash2, ChevronDown, ChevronUp, Save, GripVertical,
  UtensilsCrossed, Link2, Copy, ExternalLink, ImagePlus, X,
  Pencil, Check, FileText, FileUp, Info, QrCode,
} from "lucide-react";

function newId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}
function emptyCategory() { return { id: newId(), name: "", items: [] }; }
function emptyItem() {
  return { id: newId(), name: "", description: "", price: "", available: true, image_base64: null };
}

const MAX_ITEM_IMG_BYTES = 900_000; // ~900KB base64
const MAX_MENU_PDF_BYTES = 100 * 1024 * 1024; // 100 MB — backend comprime si pasa de 5 MB
const PDF_COMPRESS_HINT_BYTES = 8 * 1024 * 1024; // archivos mayores se comprimen en backend

export default function MenuEditor({ onGoToQRs }) {
  const [categories, setCategories] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [businessId, setBusinessId] = useState(null);
  const [slug, setSlug] = useState("");
  const [editingSlug, setEditingSlug] = useState(false);
  const [slugDraft, setSlugDraft] = useState("");
  const [menuPdf, setMenuPdf] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfCompressing, setPdfCompressing] = useState(false);
  const pdfFileRef = useRef(null);
  const confirm = useConfirm();

  const menuUrl = businessId
    ? `${window.location.origin}/menu/${slug || businessId}`
    : null;
  const menuUrlShort = menuUrl?.replace(/^https?:\/\//, "");

  useEffect(() => {
    Promise.all([api.get("/business/me"), api.get("/business/menu")])
      .then(([bizRes, menuRes]) => {
        setBusinessId(bizRes.data.id);
        const cats = menuRes.data.categories || [];
        setCategories(cats);
        const sl = menuRes.data.slug || "";
        setSlug(sl);
        setSlugDraft(sl);
        setMenuPdf(menuRes.data.menu_pdf_base64 || null);
        const exp = {};
        cats.forEach((c) => { exp[c.id] = true; });
        setExpanded(exp);
      })
      .catch(() => toast.error("Error cargando menú"))
      .finally(() => setLoading(false));
  }, []);

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const addCategory = () => {
    const cat = emptyCategory();
    setCategories((c) => [...c, cat]);
    setExpanded((e) => ({ ...e, [cat.id]: true }));
  };
  const updateCategoryName = (id, name) =>
    setCategories((cats) => cats.map((c) => c.id === id ? { ...c, name } : c));
  const removeCategory = (id) =>
    setCategories((cats) => cats.filter((c) => c.id !== id));

  const addItem = (catId) =>
    setCategories((cats) =>
      cats.map((c) => c.id === catId ? { ...c, items: [...c.items, emptyItem()] } : c)
    );
  const updateItem = (catId, itemId, field, value) =>
    setCategories((cats) =>
      cats.map((c) =>
        c.id !== catId ? c : {
          ...c,
          items: c.items.map((it) => it.id !== itemId ? it : { ...it, [field]: value }),
        }
      )
    );
  const removeItem = (catId, itemId) =>
    setCategories((cats) =>
      cats.map((c) =>
        c.id !== catId ? c : { ...c, items: c.items.filter((it) => it.id !== itemId) }
      )
    );

  const handleItemImage = async (catId, itemId, file) => {
    if (!file) return;
    if (file.size > MAX_ITEM_IMG_BYTES) { toast.error("Imagen muy grande, máx ~900 KB"); return; }
    const b64 = await fileToBase64(file);
    updateItem(catId, itemId, "image_base64", b64);
  };

  const save = async () => {
    for (const cat of categories) {
      if (!cat.name.trim()) { toast.error("Todas las categorías necesitan nombre"); return; }
      for (const it of cat.items) {
        if (!it.name.trim()) { toast.error(`Artículo sin nombre en "${cat.name}"`); return; }
      }
    }
    setBusy(true);
    try {
      // Use the draft (latest typed value) so a URL typed but not "applied"
      // still saves. slugDraft stays in sync with slug when not editing.
      const cleaned = cleanSlug(slugDraft);
      const payload = {
        slug: cleaned || null,
        categories: categories.map((cat) => ({
          ...cat,
          items: cat.items.map((it) => ({
            ...it,
            price: it.price === "" || it.price === null ? null : Number(it.price),
          })),
        })),
      };
      const res = await api.put("/business/menu", payload);
      const savedSlug = res.data.slug || "";
      setSlug(savedSlug);
      setSlugDraft(savedSlug);
      toast.success("Menú guardado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando menú");
    } finally {
      setBusy(false);
    }
  };

  const cleanSlug = (v) =>
    v.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");

  const applySlug = () => {
    const cleaned = cleanSlug(slugDraft);
    setSlug(cleaned);
    setSlugDraft(cleaned);
    setEditingSlug(false);
  };

  const handlePdfFile = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Solo archivos PDF"); return; }
    if (file.size > MAX_MENU_PDF_BYTES) { toast.error("PDF muy grande, máximo 100 MB"); return; }
    setPdfCompressing(file.size > PDF_COMPRESS_HINT_BYTES);
    setPdfBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/business/menu/pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setMenuPdf(res.data.menu_pdf_base64);
      toast.success("Menú PDF subido");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error subiendo PDF");
    } finally {
      setPdfBusy(false);
      setPdfCompressing(false);
      if (pdfFileRef.current) pdfFileRef.current.value = "";
    }
  };

  const removePdf = async () => {
    const ok = await confirm("¿Eliminar el menú en PDF? Los clientes ya no podrán verlo.");
    if (!ok) return;
    setPdfBusy(true);
    try {
      await api.delete("/business/menu/pdf");
      setMenuPdf(null);
      toast.success("PDF eliminado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error eliminando PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground py-8 text-center">Cargando menú…</div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-2xl tracking-tight">Menú / Catálogo</h3>
          <p className="text-sm text-muted-foreground mt-0.5">Tus clientes lo verán en el directorio y por tu enlace directo.</p>
        </div>
        <Button onClick={save} disabled={busy} className="btn-primary rounded-full" data-testid="menu-save-btn">
          <Save className="w-4 h-4 mr-2" />
          {busy ? "Guardando…" : "Guardar menú"}
        </Button>
      </div>

      {/* URL / QR / Slug card */}
      {businessId && (
        <div className="bg-white border border-border rounded-2xl p-5 space-y-4">
          <div className="flex items-start gap-5">
            {onGoToQRs && (
              <div className="shrink-0">
                <Button
                  onClick={onGoToQRs}
                  variant="outline"
                  className="rounded-full border-[#C4B5FD] text-[#7C3AED] hover:bg-[#F5F3FF]"
                  data-testid="menu-go-to-qrs-btn"
                >
                  <QrCode className="w-4 h-4 mr-2" /> Ver en mis QR
                </Button>
              </div>
            )}

            <div className="flex-1 min-w-0 space-y-3">
              {/* URL row */}
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Link2 className="w-3.5 h-3.5 text-[#7C3AED]" />
                  <span className="text-xs font-semibold text-[#5B21B6]">Tu menú público</span>
                </div>
                <div className="flex items-center gap-2 bg-[#F5F3FF] border border-[#DDD6FE] rounded-lg px-3 py-2">
                  <span className="text-xs font-mono text-[#5B21B6] truncate flex-1">{menuUrlShort}</span>
                  <button onClick={() => { navigator.clipboard.writeText(menuUrl); toast.success("URL copiada"); }} className="shrink-0 p-1 rounded hover:bg-[#EDE9FE]" data-testid="menu-copy-url-btn" title="Copiar">
                    <Copy className="w-3.5 h-3.5 text-[#7C3AED]" />
                  </button>
                  <a href={menuUrl} target="_blank" rel="noreferrer" className="shrink-0 p-1 rounded hover:bg-[#EDE9FE]" data-testid="menu-open-url-btn" title="Abrir">
                    <ExternalLink className="w-3.5 h-3.5 text-[#7C3AED]" />
                  </a>
                </div>
              </div>

              {/* Slug editor */}
              <div>
                <Label className="text-xs text-muted-foreground">URL personalizada</Label>
                {editingSlug ? (
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <div className="flex items-center flex-1 min-w-0 h-9 rounded-lg border border-[#DDD6FE] bg-[#F5F3FF] overflow-hidden focus-within:border-[#7C3AED] focus-within:ring-1 focus-within:ring-[#7C3AED] transition-colors">
                      <span className="text-xs font-mono text-[#8B7AA8] pl-3 pr-1 shrink-0 select-none">/menu/</span>
                      <input
                        value={slugDraft}
                        onChange={(e) => setSlugDraft(e.target.value)}
                        placeholder="mi-negocio"
                        className="flex-1 min-w-0 h-full bg-transparent text-sm font-mono text-[#5B21B6] outline-none pr-2"
                        autoFocus
                        onKeyDown={(e) => { if (e.key === "Enter") applySlug(); if (e.key === "Escape") { setSlugDraft(slug); setEditingSlug(false); } }}
                        data-testid="menu-slug-input"
                      />
                    </div>
                    <button onClick={applySlug} className="shrink-0 h-9 w-9 grid place-items-center rounded-lg bg-[#7C3AED] text-white hover:bg-[#5B21B6] transition-colors" title="Listo"><Check className="w-4 h-4" /></button>
                    <button onClick={() => { setSlugDraft(slug); setEditingSlug(false); }} className="shrink-0 h-9 w-9 grid place-items-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors" title="Cancelar"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <button
                    onClick={() => setEditingSlug(true)}
                    className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-[#7C3AED] hover:text-[#5B21B6] hover:underline"
                    data-testid="menu-slug-edit-btn"
                  >
                    <Pencil className="w-3 h-3" />
                    {slug ? `Cambiar "${slug}"` : "Personalizar URL (ej. tacos-el-sabor)"}
                  </button>
                )}
                {/* Info alert */}
                <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                  <Info className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-[11px] leading-snug text-amber-700">
                    Solo letras, números y guiones. Los cambios se guardan al presionar <span className="font-semibold">"Guardar menú"</span>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF menu card */}
      <div className="bg-white border border-border rounded-2xl p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#F5F3FF] border border-[#DDD6FE] flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5 text-[#7C3AED]" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold">Menú en PDF (opcional)</div>
              <p className="text-xs text-muted-foreground">
                {menuPdf
                  ? "Tus clientes verán un botón para abrir tu PDF."
                  : "¿Ya tienes tu menú en PDF? Súbelo (máx 100 MB, se comprime automáticamente)."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {menuPdf && (
              <>
                <button
                  type="button"
                  onClick={() => openBase64Pdf(menuPdf)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-[#7C3AED] hover:underline"
                  data-testid="menu-pdf-view-btn"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Ver PDF
                </button>
                <button
                  onClick={removePdf}
                  disabled={pdfBusy}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-500 hover:underline disabled:opacity-50"
                  data-testid="menu-pdf-delete-btn"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Eliminar
                </button>
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              disabled={pdfBusy}
              onClick={() => pdfFileRef.current?.click()}
              className="rounded-full"
              data-testid="menu-pdf-upload-btn"
            >
              <FileUp className="w-4 h-4 mr-2" />
              {pdfBusy ? (pdfCompressing ? "Subiendo y comprimiendo…" : "Subiendo…") : menuPdf ? "Reemplazar PDF" : "Subir PDF"}
            </Button>
          </div>
        </div>
        <input
          ref={pdfFileRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => handlePdfFile(e.target.files?.[0])}
          data-testid="menu-pdf-file-input"
        />
      </div>

      {/* Empty state */}
      {categories.length === 0 && (
        <div className="border-2 border-dashed border-border rounded-2xl p-10 text-center text-muted-foreground">
          <UtensilsCrossed className="w-8 h-8 mx-auto mb-3 opacity-40" />
          <p className="text-sm">Sin categorías. Agrega una para empezar.</p>
        </div>
      )}

      {/* Categories */}
      {categories.map((cat) => (
        <div key={cat.id} className="bg-white border border-border rounded-2xl overflow-hidden" data-testid={`menu-cat-${cat.id}`}>
          {/* Category header */}
          <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border bg-[#FAFAF9]">
            <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
            <Input
              value={cat.name}
              onChange={(e) => updateCategoryName(cat.id, e.target.value)}
              placeholder="Nombre de categoría (ej. Tacos, Bebidas)"
              className="font-semibold border-0 bg-transparent shadow-none focus-visible:ring-0 px-0 flex-1 text-sm"
              data-testid={`menu-cat-name-${cat.id}`}
            />
            <span className="text-xs text-muted-foreground shrink-0 tabular-nums">{cat.items.length}</span>
            <button onClick={() => toggleExpand(cat.id)} className="p-1 rounded hover:bg-muted" data-testid={`menu-cat-toggle-${cat.id}`}>
              {expanded[cat.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            <button onClick={() => removeCategory(cat.id)} className="p-1 rounded hover:bg-rose-50 text-rose-400 hover:text-rose-600" data-testid={`menu-cat-delete-${cat.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {/* Items */}
          {expanded[cat.id] && (
            <div className="divide-y divide-border">
              {cat.items.map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  catId={cat.id}
                  onUpdate={updateItem}
                  onRemove={removeItem}
                  onImage={handleItemImage}
                />
              ))}
              <div className="px-4 py-3">
                <button onClick={() => addItem(cat.id)} className="text-sm text-[#7C3AED] font-medium inline-flex items-center gap-1.5 hover:underline" data-testid={`menu-add-item-${cat.id}`}>
                  <Plus className="w-4 h-4" /> Agregar artículo
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Add category */}
      <button
        onClick={addCategory}
        className="w-full border-2 border-dashed border-[#C4B5FD] rounded-2xl py-4 text-[#7C3AED] font-semibold text-sm hover:bg-[#F5F3FF] transition-colors inline-flex items-center justify-center gap-2"
        data-testid="menu-add-category-btn"
      >
        <Plus className="w-4 h-4" /> Agregar categoría
      </button>
    </div>
  );
}

function ItemRow({ item, catId, onUpdate, onRemove, onImage }) {
  const fileRef = useRef(null);

  return (
    <div className="px-4 py-4 flex items-start gap-3" data-testid={`menu-item-${item.id}`}>
      {/* Image thumbnail / upload */}
      <div className="shrink-0">
        {item.image_base64 ? (
          <div className="relative w-14 h-14 rounded-xl overflow-hidden border border-border group">
            <img src={item.image_base64} alt="" className="w-full h-full object-cover" />
            <button
              onClick={() => onUpdate(catId, item.id, "image_base64", null)}
              className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
              title="Quitar imagen"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => fileRef.current?.click()}
            className="w-14 h-14 rounded-xl border-2 border-dashed border-border hover:border-[#C4B5FD] hover:bg-[#F5F3FF] flex flex-col items-center justify-center gap-1 transition-colors"
            title="Subir imagen"
            data-testid={`menu-item-img-btn-${item.id}`}
          >
            <ImagePlus className="w-4 h-4 text-muted-foreground" />
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => onImage(catId, item.id, e.target.files?.[0])}
        />
      </div>

      {/* Fields */}
      <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
        {/* Name */}
        <div className="sm:col-span-2">
          <Input
            value={item.name}
            onChange={(e) => onUpdate(catId, item.id, "name", e.target.value)}
            placeholder="Nombre del artículo *"
            className="font-medium"
            data-testid={`menu-item-name-${item.id}`}
          />
        </div>
        {/* Description */}
        <div className="sm:col-span-2">
          <Input
            value={item.description || ""}
            onChange={(e) => onUpdate(catId, item.id, "description", e.target.value)}
            placeholder="Descripción (opcional)"
            className="text-sm text-muted-foreground"
            data-testid={`menu-item-desc-${item.id}`}
          />
        </div>
        {/* Price */}
        <div>
          <Input
            type="number"
            min="0"
            step="0.5"
            value={item.price ?? ""}
            onChange={(e) => onUpdate(catId, item.id, "price", e.target.value)}
            placeholder="Precio MXN"
            data-testid={`menu-item-price-${item.id}`}
          />
        </div>
        {/* Available toggle */}
        <div className="flex items-center gap-2">
          <Switch
            checked={item.available}
            onCheckedChange={(v) => onUpdate(catId, item.id, "available", v)}
            data-testid={`menu-item-available-${item.id}`}
          />
          <span className={`text-xs font-medium ${item.available ? "text-emerald-600" : "text-zinc-400"}`}>
            {item.available ? "Disponible" : "No disponible"}
          </span>
        </div>
      </div>

      {/* Delete */}
      <button
        onClick={() => onRemove(catId, item.id)}
        className="shrink-0 p-1.5 mt-0.5 rounded hover:bg-rose-50 text-rose-400 hover:text-rose-600"
        data-testid={`menu-item-delete-${item.id}`}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}
